/**
 * Sheet „Passwort ändern" (`account-password-sheet`, Spec 25, Oberfläche A) —
 * für **jeden** angemeldeten Nutzer im Kontomenü (`account-password`),
 * zwischen Zugriffsverwaltung und Abmelden. `POST /access/me/password` ohne
 * aktuelles Passwort (R4): Die angemeldete Sitzung ist der Nachweis.
 *
 * **D1 (Entscheidung Lukas):** Das Wiederholfeld steht **nur hier**, nicht in
 * den Admin-Sheets (`AccessInviteSheet`, `AccessPersonPasswordSheet`) — wer
 * sich selbst vertippt, kommt ohne Mail (E1) nicht mehr allein hinein; der
 * Administrator liest dagegen mit, während er tippt.
 *
 * **B5 (Designabnahme):** `type="password"`, `autocomplete="new-password"` —
 * anders als die Admin-Passwortfelder (`PasswordRevealField`, dort bewusst
 * `type="text"`/`autocomplete="off"`, damit der Browser kein Speichern für
 * eine fremde Person anbietet). Dazu ein verstecktes Feld
 * `autocomplete="username"` mit der eigenen E-Mail — der Passwortmanager
 * ordnet den neuen Eintrag sonst keinem Konto zu.
 *
 * Kein Umschalter „Anzeigen" (B6 nennt ihn nur für die Admin-Felder) — hier
 * tippt jeder sein eigenes, gemerktes Passwort zweimal; das Wiederholfeld
 * ersetzt die Notwendigkeit, mitzulesen.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { FIELD_CLASS } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { useChangeOwnPassword } from '@/features/access/api'

// Feldfehler wie in `PasswordRevealField`: iOS bildet dieselben Kennungen
// aus Präfix und Feld (`check-invariants` vergleicht die Namen).
const ERROR_PREFIX = 'account-password-error'

interface AccountPasswordSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly email: string
  readonly onSaved: () => void
}

export function AccountPasswordSheet({ open, onOpenChange, email, onSaved }: AccountPasswordSheetProps) {
  const changeMutation = useChangeOwnPassword()
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [repeatError, setRepeatError] = useState<string | undefined>(undefined)
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const newPasswordRef = useRef<HTMLInputElement>(null)

  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setNewPassword('')
      setRepeatPassword('')
      setRepeatError(undefined)
      setPasswordError(undefined)
      setGeneralError(undefined)
    }
  }

  async function submit() {
    setRepeatError(undefined)
    setPasswordError(undefined)
    setGeneralError(undefined)

    // Eingabeprüfung, keine Fachregel — nichts wird gesendet, solange beide
    // Felder nicht übereinstimmen (Spec 25, Oberfläche A).
    if (newPassword !== repeatPassword) {
      setRepeatError('Die Passwörter stimmen nicht überein.')
      return
    }

    try {
      await changeMutation.mutateAsync(newPassword)
      onOpenChange(false)
      onSaved()
    } catch (error) {
      if (error instanceof ApiError && error.field === 'password') {
        setPasswordError(error.message)
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          newPasswordRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[420px] md:data-[side=right]:!max-w-[420px]"
      >
        <form
          data-testid="account-password-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Passwort ändern
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            {/* Für den Passwortmanager: ordnet das neue Passwort dem
             * angemeldeten Konto zu, ohne selbst bedienbar zu sein. */}
            <input type="text" name="username" autoComplete="username" value={email} readOnly hidden />

            <label className="text-label text-muted flex flex-col gap-1" htmlFor="account-password-new">
              Neues Passwort
              <input
                id="account-password-new"
                ref={newPasswordRef}
                data-testid="account-password-new"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                aria-invalid={passwordError !== undefined}
                aria-describedby={passwordError !== undefined ? `${ERROR_PREFIX}-password` : undefined}
                onChange={(event) => {
                  setNewPassword(event.target.value)
                }}
                className={`text-text ${FIELD_CLASS}`}
              />
            </label>
            {passwordError !== undefined && (
              <p
                id={`${ERROR_PREFIX}-password`}
                data-testid={`${ERROR_PREFIX}-password`}
                role="alert"
                className="text-label text-status-overdue"
              >
                {passwordError}
              </p>
            )}

            <label className="text-label text-muted flex flex-col gap-1" htmlFor="account-password-repeat">
              Passwort wiederholen
              <input
                id="account-password-repeat"
                data-testid="account-password-repeat"
                type="password"
                autoComplete="new-password"
                required
                value={repeatPassword}
                aria-invalid={repeatError !== undefined}
                aria-describedby={repeatError !== undefined ? `${ERROR_PREFIX}-repeat` : undefined}
                onChange={(event) => {
                  setRepeatPassword(event.target.value)
                }}
                className={`text-text ${FIELD_CLASS}`}
              />
            </label>
            {repeatError !== undefined && (
              <p
                id={`${ERROR_PREFIX}-repeat`}
                data-testid={`${ERROR_PREFIX}-repeat`}
                role="alert"
                className="text-label text-status-overdue"
              >
                {repeatError}
              </p>
            )}
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="account-password-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="account-password-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="account-password-save"
                disabled={changeMutation.isPending}
                aria-busy={changeMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Speichern
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
